/*!
 * Zig Jig Custom Design cart lock
 * ---------------------------------
 * A custom jig design is added to the cart as multiple line items (one per size),
 * all sharing the same `_Custom Design ID` line-item property. This script:
 *   1. In the cart DOM (page + drawer), disables per-line quantity edits and
 *      per-line "remove" for custom-jig lines, and injects a single
 *      "Remove full design" button per group.
 *   2. Intercepts /cart/change(.js|.json) and /cart/update(.js|.json) fetch/XHR
 *      calls so the URL/API workaround cannot alter a custom-jig line's quantity
 *      or remove a single size out of the group.
 *
 * Safe to load on every page — a no-op unless custom-jig lines exist.
 */
(function () {
  'use strict';

  var CD_PROP = '_Custom Design ID';
  var LOCK_ATTR = 'data-zzc-locked';
  var GROUP_ATTR = 'data-zzc-design';
  var STATE = { lines: [], byKey: {}, byDesign: {} };

  // ---------- Cart data ---------------------------------------------------

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); });
  }

  function indexCart(cart) {
    STATE.lines = (cart && cart.items) || [];
    STATE.byKey = {};
    STATE.byDesign = {};
    STATE.lines.forEach(function (item) {
      var props = item.properties || {};
      var design = props[CD_PROP];
      STATE.byKey[item.key] = { item: item, design: design || null };
      if (design) {
        (STATE.byDesign[design] = STATE.byDesign[design] || []).push(item);
      }
    });
    return cart;
  }

  function isCustomKey(key) {
    var e = STATE.byKey[key];
    return !!(e && e.design);
  }
  function designForKey(key) {
    var e = STATE.byKey[key];
    return e ? e.design : null;
  }
  function keysForDesign(design) {
    return (STATE.byDesign[design] || []).map(function (i) { return i.key; });
  }

  // ---------- Network guards ----------------------------------------------

  function parseCartUrl(u) {
    if (!u) return null;
    try {
      var url = new URL(u, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      var p = url.pathname.replace(/\/+$/, '');
      if (/\/cart\/change(?:\.js|\.json)?$/.test(p)) return 'change';
      if (/\/cart\/update(?:\.js|\.json)?$/.test(p)) return 'update';
      return null;
    } catch (e) { return null; }
  }

  function readBody(body) {
    // Returns { updates: {key:qty}, lineIndex1: n, lineQty: n, keyId: str, keyQty: n }
    var out = {};
    if (!body) return out;
    try {
      if (typeof body === 'string') {
        try { return normalize(JSON.parse(body)); } catch (_) {}
        var params = new URLSearchParams(body);
        return paramsToObj(params);
      }
      if (body instanceof URLSearchParams) return paramsToObj(body);
      if (body instanceof FormData) {
        var fd = {}; body.forEach(function (v, k) { fd[k] = v; });
        return normalize(fd);
      }
      if (typeof body === 'object') return normalize(body);
    } catch (e) {}
    return out;
  }
  function paramsToObj(params) {
    var o = {};
    params.forEach(function (v, k) {
      var m = k.match(/^updates\[(.+)\]$/);
      if (m) { o.updates = o.updates || {}; o.updates[m[1]] = Number(v); }
      else o[k] = v;
    });
    return normalize(o);
  }
  function normalize(o) {
    var r = {};
    if (o.updates && typeof o.updates === 'object') r.updates = o.updates;
    if (o.line != null) r.line = Number(o.line);
    if (o.quantity != null) r.quantity = Number(o.quantity);
    if (o.id != null) r.id = String(o.id);
    return r;
  }

  // Sync-safe evaluation: uses cached STATE. STATE is refreshed after each mutation elsewhere.
  function classifyCartOp(op, payload) {
    // Return { block: bool, reason?, groupRemove?: designId }
    if (!STATE.lines.length) return { block: false };
    var affects = [];
    if (op === 'update' && payload.updates) {
      Object.keys(payload.updates).forEach(function (k) {
        var qty = Number(payload.updates[k]);
        // updates can be keyed by variant id OR line-item key
        STATE.lines.forEach(function (li, idx) {
          if (String(k) === String(li.key) || String(k) === String(li.variant_id) || String(k) === String(li.id) || String(k) === String(idx + 1)) {
            affects.push({ line: li, newQty: qty });
          }
        });
      });
    } else if (op === 'change') {
      var target = null;
      if (payload.id) {
        STATE.lines.forEach(function (li) {
          if (String(li.key) === String(payload.id) || String(li.variant_id) === String(payload.id)) target = li;
        });
      } else if (payload.line) {
        target = STATE.lines[payload.line - 1] || null;
      }
      if (target && payload.quantity != null) affects.push({ line: target, newQty: Number(payload.quantity) });
    }
    var customChanges = affects.filter(function (a) { return (a.line.properties || {})[CD_PROP]; });
    if (!customChanges.length) return { block: false };

    // Group by design
    var byDesign = {};
    customChanges.forEach(function (a) {
      var d = a.line.properties[CD_PROP];
      (byDesign[d] = byDesign[d] || []).push(a);
    });
    var designs = Object.keys(byDesign);

    // If exactly one design and every one of its lines is being set to 0 → allow full remove
    for (var i = 0; i < designs.length; i++) {
      var d = designs[i];
      var groupKeys = keysForDesign(d);
      var changes = byDesign[d];
      var settingAllToZero = changes.length === groupKeys.length && changes.every(function (c) { return c.newQty === 0; });
      if (!settingAllToZero) {
        return {
          block: true,
          reason: 'Custom Zig Jig designs are a set — quantity per size is fixed at design time. Reopen the customizer to adjust, or remove the whole design.'
        };
      }
    }
    return { block: false };
  }

  // Wrap fetch
  var origFetch = window.fetch;
  if (origFetch && !origFetch.__zzcWrapped) {
    var wrapped = function (input, init) {
      var url = (typeof input === 'string' || input instanceof URL) ? String(input) : (input && input.url);
      var op = parseCartUrl(url);
      if (op) {
        var body = init && init.body;
        if (!body && typeof input === 'object' && input !== null && 'body' in input) body = input.body;
        var payload = readBody(body);
        var v = classifyCartOp(op, payload);
        if (v.block) {
          notify(v.reason);
          return Promise.reject(new Error(v.reason));
        }
      }
      return origFetch.apply(this, arguments).then(function (resp) {
        if (op && resp && resp.ok) refreshSoon();
        return resp;
      });
    };
    wrapped.__zzcWrapped = true;
    window.fetch = wrapped;
  }

  // Wrap XHR
  var XP = XMLHttpRequest.prototype;
  if (XP && !XP.__zzcWrapped) {
    var origOpen = XP.open;
    var origSend = XP.send;
    XP.open = function (method, url) {
      this.__zzcOp = parseCartUrl(url);
      return origOpen.apply(this, arguments);
    };
    XP.send = function (body) {
      if (this.__zzcOp) {
        var payload = readBody(body);
        var v = classifyCartOp(this.__zzcOp, payload);
        if (v.block) {
          notify(v.reason);
          var self = this;
          setTimeout(function () {
            try {
              Object.defineProperty(self, 'readyState', { value: 4, configurable: true });
              Object.defineProperty(self, 'status', { value: 422, configurable: true });
              Object.defineProperty(self, 'responseText', { value: JSON.stringify({ description: v.reason }), configurable: true });
              if (typeof self.onreadystatechange === 'function') self.onreadystatechange();
              if (typeof self.onload === 'function') self.onload();
              self.dispatchEvent(new Event('load'));
              self.dispatchEvent(new Event('loadend'));
            } catch (e) {}
          }, 0);
          return;
        }
        var self2 = this;
        this.addEventListener('load', function () { if (self2.status >= 200 && self2.status < 300) refreshSoon(); });
      }
      return origSend.apply(this, arguments);
    };
    XP.__zzcWrapped = true;
  }

  // ---------- DOM lock ---------------------------------------------------

  function ensureStyle() {
    if (document.getElementById('zzc-cart-lock-style')) return;
    var s = document.createElement('style');
    s.id = 'zzc-cart-lock-style';
    s.textContent = [
      '[' + LOCK_ATTR + '] .quantity, [' + LOCK_ATTR + '] cart-remove-button, [' + LOCK_ATTR + '] .cart-item__quantity-wrapper .quantity, [' + LOCK_ATTR + '] .cart-drawer__quantity, [' + LOCK_ATTR + '] .product-option[data-remove], [' + LOCK_ATTR + '] a.button--tertiary { display:none !important; }',
      '.zzc-locked-qty { display:inline-flex; align-items:center; gap:.5rem; font-weight:600; font-size:1.4rem; padding:.4rem .8rem; border:1px solid rgba(0,0,0,.15); border-radius:.4rem; background:rgba(0,0,0,.03); }',
      '.zzc-locked-qty .zzc-badge { font-size:1.1rem; opacity:.7; text-transform:uppercase; letter-spacing:.05em; }',
      '.zzc-remove-design { display:inline-flex; align-items:center; gap:.4rem; margin-top:.6rem; font-size:1.3rem; text-decoration:underline; background:none; border:none; padding:0; cursor:pointer; color:currentColor; }',
      '.zzc-remove-design[disabled]{opacity:.5;cursor:default}',
      '.zzc-notice { position:fixed; z-index:9999; left:50%; bottom:2rem; transform:translateX(-50%); background:#181918; color:#fff; padding:1rem 1.6rem; border-radius:.6rem; font-size:1.4rem; max-width:min(90vw,44rem); text-align:center; box-shadow:0 6px 24px rgba(0,0,0,.25); }'
    ].join('\n');
    document.head.appendChild(s);
  }

  var noticeTimer = null;
  function notify(msg) {
    ensureStyle();
    var el = document.querySelector('.zzc-notice');
    if (!el) { el = document.createElement('div'); el.className = 'zzc-notice'; document.body.appendChild(el); }
    el.textContent = msg;
    el.style.opacity = '1';
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(function () { el.style.transition = 'opacity .4s'; el.style.opacity = '0'; }, 4500);
  }

  function findLineKey(row) {
    if (!row) return null;
    var explicit = row.getAttribute('data-line-key') || row.getAttribute('data-key') || row.getAttribute('data-cart-item-key');
    if (explicit) return explicit;
    // Dawn theme: id="CartItem-1", quantity inputs have data-index and links have ?line=
    var link = row.querySelector('a.cart-item__name[href*="cart_change"], a[href*="/cart/change"], form[action*="/cart/change"]');
    if (link) {
      var href = link.getAttribute('href') || link.getAttribute('action') || '';
      var m = href.match(/[?&]id=([^&]+)/);
      if (m) return decodeURIComponent(m[1]);
    }
    var input = row.querySelector('input[name="updates[]"], input[name^="updates["], input.js-qty__num, input[type="number"]');
    if (input) {
      var nm = input.getAttribute('name') || '';
      var m2 = nm.match(/updates\[(.+?)\]/);
      if (m2) return m2[1];
      var dk = input.getAttribute('data-quantity-variant-id') || input.getAttribute('data-key');
      if (dk) return dk;
    }
    return null;
  }

  function locateCartRows() {
    // Multiple selectors to cover Dawn, cart-drawer, and common variants
    var sel = [
      'tr.cart-item',
      '.cart-items tbody tr',
      '.cart-item',
      '.cart__row',
      '.cart-drawer-item',
      '.cart-drawer__item',
      'cart-drawer-items .cart-item',
      'cart-items .cart-item'
    ].join(',');
    return Array.prototype.slice.call(document.querySelectorAll(sel));
  }

  function lockRow(row, key, design) {
    if (row.getAttribute(LOCK_ATTR)) return;
    row.setAttribute(LOCK_ATTR, '1');
    row.setAttribute(GROUP_ATTR, design);

    // Disable qty inputs so user can't type
    Array.prototype.forEach.call(row.querySelectorAll('input[type="number"], input[name^="updates["]'), function (inp) {
      inp.setAttribute('readonly', 'readonly');
      inp.setAttribute('tabindex', '-1');
      inp.style.pointerEvents = 'none';
    });

    // Insert locked-qty badge next to (or in place of) the qty control
    var host = row.querySelector('.cart-item__quantity-wrapper, .quantity, .cart-drawer__quantity, td.cart-item__quantity, .product-quantity, .js-qty') || row;
    var currentQty = 0;
    var qInput = row.querySelector('input[type="number"], input[name^="updates["]');
    if (qInput) currentQty = Number(qInput.value) || 0;
    if (!currentQty) {
      var lineData = STATE.byKey[key] && STATE.byKey[key].item;
      if (lineData) currentQty = lineData.quantity;
    }
    if (host && !row.querySelector('.zzc-locked-qty')) {
      var badge = document.createElement('span');
      badge.className = 'zzc-locked-qty';
      badge.innerHTML = '<span>' + currentQty + ' dozen</span><span class="zzc-badge">custom set</span>';
      host.appendChild(badge);
    }

    // Add "Remove full design" once per group in the first row of that design
    var firstRow = document.querySelector('[' + GROUP_ATTR + '="' + cssEscape(design) + '"]');
    if (firstRow === row && !row.querySelector('.zzc-remove-design')) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'zzc-remove-design';
      btn.textContent = 'Remove full custom design';
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        btn.disabled = true;
        removeDesign(design).catch(function () {
          btn.disabled = false;
          notify('Could not remove the design. Please refresh and try again.');
        });
      });
      var target = row.querySelector('.cart-item__details, .cart-item__meta, td.cart-item__details, .cart-drawer-item__details, .cart-drawer__item-details') || row;
      target.appendChild(btn);
    }
  }

  function removeDesign(design) {
    var keys = keysForDesign(design);
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    // Use origFetch so we don't have to whitelist our own request in the guard;
    // the guard already allows an "all keys → 0" op for a single design.
    return (origFetch || window.fetch)('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) {
      if (!r.ok) throw new Error('bad status');
      // Trigger a full cart refresh so Dawn/drawer re-render
      return refreshCart(true);
    });
  }

  function cssEscape(s) {
    if (window.CSS && CSS.escape) return CSS.escape(s);
    return String(s).replace(/[^a-zA-Z0-9_-]/g, function (c) { return '\\' + c; });
  }

  function paintRows() {
    ensureStyle();
    var rows = locateCartRows();
    rows.forEach(function (row) {
      var key = findLineKey(row);
      if (!key) return;
      var design = designForKey(key);
      if (!design) return;
      lockRow(row, key, design);
    });
  }

  // ---------- Refresh cycle ----------------------------------------------

  var refreshQueued = false;
  function refreshSoon() {
    if (refreshQueued) return;
    refreshQueued = true;
    setTimeout(function () { refreshQueued = false; refreshCart(false); }, 60);
  }

  function refreshCart(reloadIfCartPage) {
    return fetchCart().then(indexCart).then(function () {
      paintRows();
      if (reloadIfCartPage && /\/cart\b/.test(location.pathname)) {
        // Easiest way to guarantee the cart page re-renders after a bulk remove
        window.location.reload();
      }
    }).catch(function () {});
  }

  // Observe DOM for cart drawer swaps / cart page re-renders
  var mo = new MutationObserver(function () { paintRows(); });
  function boot() {
    ensureStyle();
    refreshCart(false);
    mo.observe(document.body, { childList: true, subtree: true });
    // Cart drawer open triggers
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('a[href="/cart"], a[href$="/cart"], .cart-icon, [data-cart-drawer-trigger]')) {
        setTimeout(function () { refreshCart(false); }, 300);
      }
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
